import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { EyeIcon } from '@heroicons/react/24/outline';
import PageLayout from '../components/PageLayout';
import PageBanner from '../components/PageBanner';
import SEO from '../components/SEO';
import API from '../api';
import { useLanguage } from '../context/LanguageContext';

const BASE = process.env.REACT_APP_API_URL?.replace('/api', '') ?? 'http://localhost:5000';

export default function Products() {
  const { t } = useLanguage();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    API.get('/categories').then(r => setCategories(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <PageLayout>
      <SEO title={t('common.products')} description="Browse Sankyo Machine Tools' full product range by category — cutting tools, machine tools, factory equipment, measurement instruments, and more." />
      <PageBanner en="OUR PRODUCTS" title={t('common.products')} breadcrumbs={[{ label: t('common.products') }]} />

      <section className="pt-6 pb-20 lg:pb-28 bg-white">
        <div className="w-[90%] max-w-7xl mx-auto">
          <h2 className="section-title mb-10">{t('products.browseByCategory')}</h2>

          {loading ? (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-5 gap-y-10">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="aspect-[4/3] bg-gray-100 animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-5 gap-y-12">
              {categories.map((cat, i) => (
                <Link key={cat.id} to={`/products/${cat.slug}`}
                  className="group block fade-up"
                  style={{ animationDelay: `${i * 0.06}s` }}>
                  <div className="aspect-[4/3] bg-[#f5f5f5] overflow-hidden relative mb-4">
                    {cat.image_url ? (
                      <img src={cat.image_url.startsWith('/') ? `${BASE}${cat.image_url}` : cat.image_url}
                        alt={cat.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-charcoal">
                        <span className="text-white/20 text-6xl font-display font-extrabold">{cat.name[0]}</span>
                      </div>
                    )}
                    {cat.unique_view_count > 0 && (
                      <div className="absolute top-3 right-3 flex items-center gap-1 bg-black/50 text-white text-xs font-medium px-2 py-1 rounded-full backdrop-blur-sm">
                        <EyeIcon className="w-3.5 h-3.5" />
                        {cat.unique_view_count}
                      </div>
                    )}
                  </div>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h3 className="font-bold text-ink text-base sm:text-lg leading-snug group-hover:text-primary transition-colors mb-1.5">{cat.name}</h3>
                      {cat.description && <p className="text-xs sm:text-sm text-gray-500 line-clamp-2 leading-relaxed">{cat.description}</p>}
                    </div>
                    <span className="mt-2 w-2 h-2 shrink-0 border-t-2 border-r-2 border-primary rotate-45 group-hover:translate-x-1 transition-transform" />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </PageLayout>
  );
}
